import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchGraph,
  fetchLint,
  fetchStats,
  type KnowledgeGraph,
  type KnowledgeStats,
  type LintReport,
} from '../api/knowledge';
import { fetchFindings, type FindingsResponse } from '../api/findings';
import { StatCard } from '../components/dashboard/StatCard';
import { DomainPill } from '../components/dashboard/DomainPill';
import { FindingsSummary } from '../components/dashboard/FindingsSummary';
import { LintSummary } from '../components/dashboard/LintSummary';
import { GraphView } from '../components/graph/GraphView';
import { deriveDomainInfo } from '../components/graph/domain';
import './DashboardPage.css';
import { useInstanceName } from '../InstanceName';

export function DashboardPage() {
  const instanceName = useInstanceName();
  const navigate = useNavigate();
  const [graph, setGraph] = useState<KnowledgeGraph | null>(null);
  const [stats, setStats] = useState<KnowledgeStats | null>(null);
  const [lint, setLint] = useState<LintReport | null>(null);
  const [findings, setFindings] = useState<FindingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchGraph(),
      fetchStats(),
      fetchLint(),
      fetchFindings({ status: 'active' }).catch(() => null),
    ])
      .then(([g, s, l, f]) => {
        if (cancelled) return;
        setGraph(g);
        setStats(s);
        setLint(l);
        setFindings(f);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="dashboard">
        <div className="dashboard__error">{error}</div>
      </div>
    );
  }

  const domainInfo = graph ? deriveDomainInfo(graph.nodes) : new Map();
  const totalIssues = lint
    ? lint.contradictions.length +
      lint.orphans.length +
      lint.stale.length +
      lint.stuck_stubs.length +
      lint.broken_sources.length
    : 0;

  function findingsAccent(f: FindingsResponse | null): string | undefined {
    if (!f) return undefined;
    const sev = f.by_severity || {};
    if (sev.URGENT) return '#ef4444';
    if (sev.ATTEND) return '#fb923c';
    if (sev.WATCH) return '#facc15';
    return f.count > 0 ? '#818cf8' : '#34d399';
  }

  function findingsTrailing(f: FindingsResponse | null): string | undefined {
    if (!f) return undefined;
    const parts: string[] = [];
    for (const sev of ['URGENT', 'ATTEND', 'WATCH', 'ROUTINE']) {
      const n = f.by_severity?.[sev];
      if (n) parts.push(`${sev[0]}${sev.slice(1).toLowerCase()}: ${n}`);
    }
    return parts.length > 0 ? parts.join(' · ') : 'all clear';
  }

  return (
    <div className="dashboard">
      <header className="dashboard__header">
        <div className="dashboard__title">Dashboard</div>
        <div className="dashboard__subtitle">
          {instanceName} knowledge overview
        </div>
      </header>

      <section className="dashboard__stats">
        <StatCard
          label="Pages"
          value={stats ? stats.total_pages : '—'}
          trailing={stats ? `${stats.mature_domain_count} mature domains` : undefined}
        />
        <StatCard
          label="Avg quality"
          value={stats ? stats.avg_quality.toFixed(2) : '—'}
          accent="var(--color-accent)"
          trailing={
            stats
              ? `${stats.quality_distribution.canonical} canonical · ${stats.quality_distribution.mature} mature`
              : undefined
          }
        />
        <StatCard
          label="Issues"
          value={lint ? totalIssues : '—'}
          accent={totalIssues > 0 ? '#fb923c' : '#34d399'}
          trailing={lint ? `${lint.pages_scanned} scanned` : undefined}
        />
        <StatCard
          label="Findings"
          value={findings ? findings.count : '—'}
          accent={findingsAccent(findings)}
          trailing={findingsTrailing(findings)}
        />
      </section>

      <section className="dashboard__grid">
        <div className="dashboard__graph glass">
          <div className="dashboard__panel-head">
            <div className="label">Knowledge Map</div>
            <button
              type="button"
              className="dashboard__open"
              onClick={() => navigate('/graph')}
            >
              Open full graph →
            </button>
          </div>
          <div className="dashboard__graph-preview">
            {graph ? (
              <GraphView graph={graph} compact />
            ) : (
              <div className="dashboard__loading">Loading...</div>
            )}
          </div>
        </div>

        <div className="dashboard__side">
          {lint ? (
            <LintSummary report={lint} />
          ) : (
            <div className="glass dashboard__loading">Loading...</div>
          )}
          {findings && (
            <FindingsSummary
              findings={findings}
              onOpenAll={() => navigate('/findings')}
            />
          )}
        </div>
      </section>

      <section className="dashboard__domains glass">
        <div className="dashboard__panel-head">
          <div className="label">Domains</div>
        </div>
        <div className="dashboard__pills">
          {Array.from(domainInfo.values()).map((info) => (
            <DomainPill
              key={info.name}
              name={info.name}
              color={info.color}
              count={info.count}
            />
          ))}
          {domainInfo.size === 0 && (
            <span className="dashboard__empty">No domains yet.</span>
          )}
        </div>
      </section>
    </div>
  );
}
